import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, type ApiError } from '../../api/client';
import { Alert } from '../../components/Field';

interface Assignment {
  id: string;
  contentType: 'COURSE' | 'PATH';
  content: { id: string; title: string; slug: string } | null;
  target: { type: string; id: string };
  isMandatory: boolean;
  dueAt: string | null;
  status: 'ACTIVE' | 'WITHDRAWN';
  note: string | null;
  createdAt: string;
  reach: { assigned: number; completed: number; overdue: number };
}

interface Preview {
  content: { type: string; title: string };
  willReach: number;
  alreadyHaveAccess: number;
  needAccess: number;
  subscription: { plan: string; seatsAvailable: number; coversContent: boolean } | null;
  canProceed: boolean;
  blockers: string[];
}

interface Option {
  id: string;
  label: string;
}

export function OrgAssignments() {
  const [items, setItems] = useState<Assignment[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api<{ items: Assignment[] }>('/org/assignments');
      setItems(response.items);
    } catch (caught) {
      setError(caught as ApiError);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function withdraw(id: string) {
    try {
      const result = await api<{ accessRevoked: number }>(`/org/assignments/${id}`, {
        method: 'DELETE',
      });
      setNotice(
        `Withdrawn. Access was revoked for ${result.accessRevoked} ` +
          `${result.accessRevoked === 1 ? 'person' : 'people'}; their progress is kept.`,
      );
      void load();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Assigned learning</h1>
        <p className="page__lede">
          Assign a course or path to a person, a team or a whole department, with a deadline.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
      {notice ? <Alert kind="success" title={notice} /> : null}

      <AssignForm
        onDone={(message) => {
          setNotice(message);
          void load();
        }}
        onError={setError}
      />

      <div className="card">
        <div className="card__head">
          <span className="card__title">Assignments</span>
        </div>

        {items === null ? (
          <div className="card__body">
            <div className="skel" style={{ height: 120 }} />
          </div>
        ) : items.length === 0 ? (
          <div className="state">
            <div className="state__art" aria-hidden="true">⊞</div>
            <h4>Nothing assigned yet</h4>
            <p>Assign a course or a path above and it will appear here.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Content</th>
                <th>Reach</th>
                <th>Done</th>
                <th>Overdue</th>
                <th>Due</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.content?.title ?? 'Removed content'}</strong>
                    <br />
                    <span className="muted">
                      {item.contentType === 'PATH' ? 'Learning path' : 'Course'}
                      {item.isMandatory ? ' · Mandatory' : ''}
                    </span>
                  </td>
                  <td>{item.reach.assigned}</td>
                  <td>{item.reach.completed}</td>
                  <td>
                    {item.reach.overdue > 0 ? (
                      <span className="pill pill--error">{item.reach.overdue}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="muted">
                    {item.dueAt ? new Date(item.dueAt).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <span
                      className={`pill pill--${item.status === 'ACTIVE' ? 'approved' : 'inactive'}`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td>
                    {item.status === 'ACTIVE' ? (
                      <button
                        className="btn btn--outline btn--sm"
                        onClick={() => void withdraw(item.id)}
                      >
                        Withdraw
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/**
 * Assigning is a two-step action on purpose: the preview says who it will
 * reach and whether the seats exist, so an administrator is never surprised
 * by a refusal after committing (BRD S-ORG-05).
 */
function AssignForm({
  onDone,
  onError,
}: {
  onDone: (message: string) => void;
  onError: (error: ApiError) => void;
}) {
  const [contentType, setContentType] = useState<'COURSE' | 'PATH'>('COURSE');
  const [contentId, setContentId] = useState('');
  const [targetType, setTargetType] = useState<'USER' | 'TEAM' | 'DEPARTMENT'>('DEPARTMENT');
  const [targetId, setTargetId] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [isMandatory, setIsMandatory] = useState(true);

  const [content, setContent] = useState<Option[]>([]);
  const [targets, setTargets] = useState<Option[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setContentId('');
    setPreview(null);
    void (async () => {
      if (contentType === 'PATH') {
        const response = await api<{ items: Array<{ id: string; title: string; status: string }> }>(
          '/admin/paths?status=PUBLISHED',
        ).catch(() => ({ items: [] }));
        setContent(response.items.map((row) => ({ id: row.id, label: row.title })));
        return;
      }
      const response = await api<{ items: Array<{ id: string; title: string }> }>(
        '/catalog/search?pageSize=100',
      ).catch(() => ({ items: [] }));
      setContent(response.items.map((row) => ({ id: row.id, label: row.title })));
    })();
  }, [contentType]);

  useEffect(() => {
    setTargetId('');
    setPreview(null);
    void (async () => {
      const path =
        targetType === 'DEPARTMENT'
          ? '/org/departments'
          : targetType === 'TEAM'
            ? '/org/teams'
            : '/org/employees';
      const response = await api<{ items: Array<{ id: string; name?: string }> }>(path).catch(
        () => ({ items: [] }),
      );
      setTargets(response.items.map((row) => ({ id: row.id, label: row.name ?? row.id })));
    })();
  }, [targetType]);

  const body = () => ({
    contentType,
    contentId,
    targetType,
    targetId,
    ...(dueAt ? { dueAt: new Date(dueAt).toISOString() } : {}),
    isMandatory,
  });

  async function runPreview(event: FormEvent) {
    event.preventDefault();
    if (!contentId || !targetId) return;
    setBusy(true);
    try {
      setPreview(await api<Preview>('/org/assignments/preview', {
        method: 'POST',
        body: JSON.stringify(body()),
      }));
    } catch (caught) {
      onError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    try {
      const created = await api<Assignment>('/org/assignments', {
        method: 'POST',
        body: JSON.stringify(body()),
      });
      setPreview(null);
      setContentId('');
      setTargetId('');
      onDone(
        `Assigned "${created.content?.title}" to ${created.reach.assigned} ` +
          `${created.reach.assigned === 1 ? 'person' : 'people'}.`,
      );
    } catch (caught) {
      onError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card__head">
        <span className="card__title">Assign learning</span>
      </div>
      <div className="card__body">
        <form className="row" onSubmit={runPreview}>
          <label className="field">
            <span className="field__label">What</span>
            <select
              className="field__input"
              value={contentType}
              onChange={(event) => setContentType(event.target.value as 'COURSE' | 'PATH')}
            >
              <option value="COURSE">Course</option>
              <option value="PATH">Learning path</option>
            </select>
          </label>

          <label className="field">
            <span className="field__label">Which</span>
            <select
              className="field__input"
              value={contentId}
              onChange={(event) => setContentId(event.target.value)}
              required
            >
              <option value="">Choose…</option>
              {content.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">To</span>
            <select
              className="field__input"
              value={targetType}
              onChange={(event) =>
                setTargetType(event.target.value as 'USER' | 'TEAM' | 'DEPARTMENT')
              }
            >
              <option value="DEPARTMENT">A department</option>
              <option value="TEAM">A team</option>
              <option value="USER">One person</option>
            </select>
          </label>

          <label className="field">
            <span className="field__label">Whom</span>
            <select
              className="field__input"
              value={targetId}
              onChange={(event) => setTargetId(event.target.value)}
              required
            >
              <option value="">Choose…</option>
              {targets.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Due by</span>
            <input
              className="field__input"
              type="date"
              value={dueAt}
              onChange={(event) => setDueAt(event.target.value)}
            />
          </label>

          <label className="filters__opt" style={{ alignSelf: 'end', paddingBottom: 10 }}>
            <input
              type="checkbox"
              checked={isMandatory}
              onChange={(event) => setIsMandatory(event.target.checked)}
            />
            Mandatory
          </label>

          <button className="btn btn--outline btn--sm" type="submit" disabled={busy}>
            Preview
          </button>
        </form>

        {preview ? (
          <div className="readiness" style={{ marginTop: 'var(--sx-space-4)', position: 'static' }}>
            <div className="readiness__head">
              {preview.content.title} → {preview.willReach}{' '}
              {preview.willReach === 1 ? 'person' : 'people'}
            </div>
            <div className="readiness__body">
              {preview.canProceed ? (
                <p className="readiness__state readiness__state--ok">
                  ✓ Ready — {preview.needAccess} need a seat,{' '}
                  {preview.alreadyHaveAccess} already have access
                  {preview.subscription
                    ? `, ${preview.subscription.seatsAvailable} available`
                    : ''}
                </p>
              ) : (
                <>
                  <p className="readiness__state readiness__state--blocked">
                    ✕ This cannot go ahead
                  </p>
                  <ul className="readiness__issues">
                    {preview.blockers.map((blocker) => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                </>
              )}

              <div className="readiness__actions">
                <button
                  className="btn btn--sm"
                  disabled={!preview.canProceed || busy}
                  onClick={() => void confirm()}
                >
                  {busy ? 'Assigning…' : 'Confirm'}
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => setPreview(null)}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
